import { useEffect, useState } from 'react';
import { Button, Col, Divider, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// services
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';

// components
import { onApproveMinor, onDeleteMinor, onGetMinorById, onRejectMinor, onUnlinkMinor } from 'modules/minors/services/minors';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import AuthActionComponent from 'components/authAction/authAction';
import FilePreview from 'components/filePreview/filePreview';
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';

// models
import { Minor } from 'modules/minors/models/minors';

//styles
import styles from './view.module.scss';
import PDFViewer from 'components/pdfViewer/pdfViewer';
import { StatusColors } from 'utils/statusColors';

function MinorViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [minorDetails, setMinorDetails] = useState<Minor>();

	// actions states
	const [isUnlinking, setIsUnlinking] = useState(false);
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);

	const [showUnlinkingConfirmation, setShowUnlinkingConfirmation] = useState(false);
	const [showApprovingConfirmation, setShowApprovingConfirmation] = useState(false);
	const [showRejectingConfirmation, setShowRejectingConfirmation] = useState(false);

	useEffect(() => {
		getMinor();
	}, []);

	const getMinor = async () => {
		try {
			setIsLoading(true);

			const response = await onGetMinorById(id as string);

			setMinorDetails(response.data.data);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/minors');
		}
	};

	const onApprove = async () => {
		try {
			setIsApproving(true);
			await onApproveMinor(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.MINOR_APPROVED'));
			setShowApprovingConfirmation(false);
			setIsApproving(false);
			getMinor();
		} catch (error) {
			setIsApproving(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.MINOR_NOT_APPROVED'));
		}
	};

	const onUnlink = async () => {
		try {
			setIsUnlinking(true);
			await onUnlinkMinor(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.MINOR_UNLINKED'));
			setShowUnlinkingConfirmation(false);
			setIsUnlinking(false);
			getMinor();
		} catch (error) {
			setIsUnlinking(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.MINOR_NOT_UNLINKED'));
		}
	};

	const onReject = async (message: string) => {
		try {
			setIsRejecting(true);
			await onRejectMinor(id!, message);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.MINOR_REJECTED'));
			setShowRejectingConfirmation(false);
			setIsRejecting(false);
			getMinor();
		} catch (error) {
			setIsRejecting(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.MINOR_NOT_REJECTED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
			</div>

			<h3 className="fw-semibold mb-4">{t('MINORS.MINOR_DETAILS')}</h3>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer`}>
					<div>
						<div className="d-flex align-items-center justify-content-between mb-4">
							<h5 className="fw-semibold text-primary mb-0">{t('MINORS.MINOR_DETAILS')}</h5>
							<div className="d-flex gap-2">
								<AuthActionComponent action="minor_update">
									{minorDetails?.status_key == 'Pending' && (
										<>
											<Button
												color="primary"
												variant="filled"
												onClick={() => {
													setShowApprovingConfirmation(true);
												}}>
												{t('MINORS.APPROVE')}
											</Button>
											<Button
												color="danger"
												variant="filled"
												onClick={() => {
													setShowRejectingConfirmation(true);
												}}>
												{t('MINORS.REJECT')}
											</Button>
										</>
									)}
									{minorDetails?.status_key == 'Approved' && (
										<Button
											type="link"
											color="danger"
											variant="filled"
											onClick={() => {
												setShowUnlinkingConfirmation(true);
											}}>
											{t('MINORS.UNLINK')}
										</Button>
									)}
								</AuthActionComponent>
							</div>
						</div>

						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{minorDetails?.minor?.avatar_preview ? (
									<img src={minorDetails?.minor?.avatar_preview} />
								) : isRTL() ? (
									minorDetails?.minor?.name?.ar?.slice(0, 1)
								) : (
									minorDetails?.minor?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? minorDetails?.minor?.name?.ar : minorDetails?.minor?.name?.en}</h5>

										{minorDetails?.minor?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${StatusColors[minorDetails?.minor?.profile_status!]}`}>
											{t(`COMMON.${minorDetails?.minor?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr', fontWeight: 500 }}>
													{`+${minorDetails?.minor?.phone_country_code} ${minorDetails?.minor?.phone_number}`}
												</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{minorDetails?.minor?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{minorDetails?.minor?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{minorDetails?.minor?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{minorDetails?.minor?.date_of_birth ? dayjs(minorDetails?.minor?.date_of_birth).format('YYYY-MM-DD') : ''}
											</Col>
											{minorDetails?.minor?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{minorDetails?.minor?.email}
												</Col>
											)}
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div>
						<h5 className="fw-semibold mb-4 text-primary">{t('MINORS.GARDIAN_DETAILS')}</h5>
						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{minorDetails?.guardianUser?.avatar_preview ? (
									<img src={minorDetails?.guardianUser?.avatar_preview} />
								) : isRTL() ? (
									minorDetails?.guardianUser?.name?.ar?.slice(0, 1)
								) : (
									minorDetails?.guardianUser?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? minorDetails?.guardianUser?.name?.ar : minorDetails?.guardianUser?.name?.en}</h5>

										{minorDetails?.guardianUser?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${StatusColors[minorDetails?.guardianUser?.profile_status!]}`}>
											{t(`COMMON.${minorDetails?.guardianUser?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr', fontWeight: 500 }}>
													{`+${minorDetails?.guardianUser?.phone_country_code} ${minorDetails?.guardianUser?.phone_number}`}
												</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{minorDetails?.guardianUser?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{minorDetails?.guardianUser?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{minorDetails?.guardianUser?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{minorDetails?.guardianUser?.date_of_birth
													? dayjs(minorDetails?.guardianUser?.date_of_birth).format('YYYY-MM-DD')
													: ''}
											</Col>
											{minorDetails?.guardianUser?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{minorDetails?.guardianUser?.email}
												</Col>
											)}
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div className="d-flex justify-content-center gap-3 flex-wrap">
						<div className="mb-4">
							<PDFViewer link={minorDetails?.family_id_document!} />
							<FilePreview
								className="flex-grow-1 mt-2"
								name={t('MINORS.FAMILY_ID_DOCUMENT')}
								downloadLink={minorDetails?.family_id_document}
							/>
						</div>

						<Divider />

						<div>
							<PDFViewer link={minorDetails?.custody_order_document!} />

							<FilePreview
								className="flex-grow-1 mt-2"
								name={t('MINORS.CUSTODY_ORDER_DOCUMENT')}
								downloadLink={minorDetails?.custody_order_document}
							/>
						</div>
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showApprovingConfirmation}
				confirmText={t('MINORS.APPROVE')}
				confirmType="primary"
				onDiscard={() => {
					setShowApprovingConfirmation(false);
				}}
				title={t('MINORS.APPROVE_TITLE')}
				description={t('MINORS.APPROVE_DESCRIPTION')}
				onConfirm={onApprove}
				isLoading={isApproving}
			/>

			<ConfirmModalComponent
				open={showUnlinkingConfirmation}
				confirmText={t('MINORS.UNLINK')}
				onDiscard={() => {
					setShowUnlinkingConfirmation(false);
				}}
				title={t('MINORS.UNLINK_TITLE')}
				description={t('MINORS.UNLINK_DESCRIPTION')}
				onConfirm={onUnlink}
				isLoading={isUnlinking}
			/>

			<ConfirmModalComponent
				open={showRejectingConfirmation}
				confirmText={t('MINORS.REJECT')}
				onDiscard={() => {
					setShowRejectingConfirmation(false);
				}}
				title={t('MINORS.REJECT_TITLE')}
				description={t('MINORS.REJECT_DESCRIPTION')}
				onConfirm={onReject}
				isLoading={isRejecting}
				inputMessage
			/>
		</>
	);
}

export default MinorViewPage;
